// Module 09 §5/§9 — the ONLY translator between the persisted `sections` JSON and the typed
// row tree. Pure: no Vue, no store, no http.
//
// The parity rule this file exists to guarantee:
//
//     serializeLayout(parseLayout(sections)) deep-equals sections
//
// It holds *by construction*, not by diligence. Every parsed node keeps `source` — the verbatim
// persisted object — and serialising spreads `source`, overwriting only the child collection it
// re-serialises. Nothing enumerates the widget keys, so a key nobody has inventoried (production
// carries 17+ beyond the contract's list) cannot be dropped.
//
// Two corollaries, both load-bearing:
//   • `visibleType` is written back exactly as it was read. It is never re-derived from `kind` —
//     the backend gates `sections_validator` on it, and production stores `visibleType:'matrix'`
//     (5 live widgets), never the `{'table','Matrix'}` split contract §3.2 assumed (0 live).
//   • A malformed child collection is left verbatim rather than replaced, so corrupt data
//     degrades to an empty cell in the UI without being rewritten on the next save.

import type {
  DashboardCell,
  DashboardRow,
  DashboardTab,
  WireCol,
  WireSection,
  WireSections,
  WireTab,
} from '../../models/layout.types'
import {
  isWidgetKind,
  type DashboardWidget,
  type DashboardWidgetWire,
  type WidgetKind,
} from '../../models/widget.types'

const DEFAULT_CELL_WIDTH = 100
const FALLBACK_ROW_NAME = null

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function asRecords(value: unknown): Record<string, unknown>[] | null {
  if (!Array.isArray(value)) return null
  return value.filter(isRecord)
}

function textOr(value: unknown, fallback: string): string {
  return typeof value === 'string' && value.length > 0 ? value : fallback
}

/**
 * The widget kind a persisted bag renders as.
 *
 * `{visibleType:'table', selectedTable:'Matrix'}` is the historical split shape — still read by
 * legacy `CustomDashboardCreateModal.vue:843`, absent from production — and normalises to
 * `matrix` here. Normalisation is read-only; `serializeWidget` re-emits the original bag.
 */
export function deriveWidgetKind(wire: DashboardWidgetWire): WidgetKind | null {
  const visibleType = wire.visibleType
  if (typeof visibleType !== 'string') return null
  if (visibleType === 'media_card') return 'card'
  if (visibleType === 'table' && wire.selectedTable === 'Matrix') return 'matrix'
  return isWidgetKind(visibleType) ? visibleType : null
}

function parseWidget(col: WireCol): DashboardWidget | null {
  if (typeof col.visibleType !== 'string' || col.visibleType.length === 0) return null
  const wire = col as DashboardWidgetWire
  return { kind: deriveWidgetKind(wire), wire }
}

function parseTab(source: WireTab, index: number): DashboardTab {
  const nested = asRecords(source.sections)
  return {
    id: textOr(source.id, `tab-${index}`),
    name: textOr(source.name, ''),
    rows: nested ? nested.map((section, i) => parseRow(section, i)) : [],
    source,
  }
}

function parseCell(source: WireCol, index: number): DashboardCell {
  const rawTabs = asRecords(source.tabs)
  return {
    id: textOr(source.id, `cell-${index}`),
    name: textOr(source.name, ''),
    width: typeof source.width === 'number' ? source.width : DEFAULT_CELL_WIDTH,
    tabs: rawTabs ? rawTabs.map((tab, i) => parseTab(tab, i)) : [],
    widget: parseWidget(source),
    source,
  }
}

function parseRow(source: WireSection, index: number): DashboardRow {
  const rawCols = asRecords(source.cols)
  return {
    id: textOr(source.id, `row-${index}`),
    name: typeof source.name === 'string' ? source.name : FALLBACK_ROW_NAME,
    cells: rawCols ? rawCols.map((col, i) => parseCell(col, i)) : [],
    source,
  }
}

/**
 * Persisted `sections` → typed rows. Never throws: a non-array payload, a non-object node, or a
 * corrupt child collection all degrade to empty rather than failing the page (ports
 * `CustomDashboardSection.normalizeSections:301-326`).
 */
export function parseLayout(sections: unknown): DashboardRow[] {
  const rows = asRecords(sections)
  if (!rows) return []
  return rows.map((section, index) => parseRow(section, index))
}

/**
 * Re-emit a child collection only when the persisted value was a real array; anything else is
 * left exactly as stored so a malformed block is never rewritten by a save.
 */
function withChildren<T extends Record<string, unknown>>(
  source: T,
  key: string,
  children: unknown[],
): T {
  if (!Array.isArray(source[key])) return { ...source }
  return { ...source, [key]: children }
}

function serializeTab(tab: DashboardTab): WireTab {
  return withChildren(tab.source, 'sections', tab.rows.map(serializeRow))
}

function serializeCell(cell: DashboardCell): WireCol {
  return withChildren(cell.source, 'tabs', cell.tabs.map(serializeTab))
}

function serializeRow(row: DashboardRow): WireSection {
  return withChildren(row.source, 'cols', row.cells.map(serializeCell))
}

/** Typed rows → the persisted `sections` payload. Inverse of {@link parseLayout}. */
export function serializeLayout(rows: readonly DashboardRow[]): WireSections {
  return rows.map(serializeRow)
}
